/**
 * LevelUpCelebration - Full-screen level up celebration effect
 *
 * Features:
 * - Full-screen overlay with radial burst
 * - Level number animates in with scale/glow
 * - Burst rings and sparkles effects
 * - Triumphant sound effect
 * - Auto-dismiss after animation
 *
 * Note: Confetti is triggered separately via the global confetti context
 * to keep celebration effects decoupled and composable.
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BurstEffect, SparkleEffect } from './ConfettiEffect';
import audioManager from '../../services/audioManager';

const LevelUpCelebration = ({
  isVisible,
  level,
  onComplete,
  autoDismissDelay = 4000,
}) => {
  const [showBurst, setShowBurst] = useState(false);
  const [showSparkles, setShowSparkles] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Play level up sound
      audioManager.playLevelUp();

      // Trigger effects in sequence
      setShowBurst(true);
      setTimeout(() => setShowSparkles(true), 400);

      // Auto-dismiss
      const dismissTimer = setTimeout(() => {
        onComplete?.();
      }, autoDismissDelay);

      return () => {
        clearTimeout(dismissTimer);
        setShowBurst(false);
        setShowSparkles(false);
      };
    }
  }, [isVisible, autoDismissDelay, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onComplete}
        >
          {/* Dark overlay with radial gradient */}
          <motion.div
            className="absolute inset-0 bg-black/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Radial glow effect */}
          <motion.div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at center, rgba(241, 196, 15, 0.3) 0%, transparent 50%)',
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [0, 2, 1.5],
              opacity: [0, 1, 0.6],
            }}
            transition={{
              duration: 1,
              times: [0, 0.4, 1],
              ease: 'easeOut',
            }}
          />

          {/* Burst rings effect */}
          <BurstEffect
            isActive={showBurst}
            ringCount={4}
            color="#f1c40f"
            size={150}
          />

          {/* Main content container */}
          <div className="relative z-10 flex flex-col items-center">
            {/* "LEVEL UP" text */}
            <motion.div
              className="text-2xl md:text-3xl font-bold tracking-[0.3em] text-accent-yellow mb-4"
              initial={{ y: -50, opacity: 0, scale: 0.5 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
                delay: 0.2,
              }}
              style={{
                textShadow: '0 0 30px rgba(241, 196, 15, 0.8), 0 0 60px rgba(241, 196, 15, 0.4)',
              }}
            >
              LEVEL UP!
            </motion.div>

            {/* Level number */}
            <motion.div
              className="relative"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15,
                delay: 0.4,
              }}
            >
              {/* Glow behind number */}
              <motion.div
                className="absolute inset-0 blur-2xl"
                style={{
                  background: 'radial-gradient(circle, rgba(241, 196, 15, 0.8) 0%, transparent 70%)',
                }}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.8, 1, 0.8],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Level number display */}
              <motion.div
                className="relative text-8xl md:text-9xl font-black"
                style={{
                  color: '#f1c40f',
                  textShadow: `
                    0 0 20px rgba(241, 196, 15, 1),
                    0 0 40px rgba(241, 196, 15, 0.8),
                    0 0 60px rgba(241, 196, 15, 0.6),
                    0 0 80px rgba(241, 196, 15, 0.4),
                    0 4px 0 #c9a30a,
                    0 8px 0 #a68508
                  `,
                }}
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                {level}
              </motion.div>

              {/* Sparkles around number */}
              <SparkleEffect
                isActive={showSparkles}
                particleCount={24}
                colors={['#f1c40f', '#f39c12', '#ffffff', '#ffeaa7']}
                size={180}
              />
            </motion.div>

            {/* Subtitle */}
            <motion.div
              className="mt-6 text-lg text-text-lighter tracking-wide"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              Your survivor grows stronger!
            </motion.div>

            {/* Click to dismiss hint */}
            <motion.div
              className="mt-8 text-sm text-text-muted"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0.5, 1] }}
              transition={{ delay: 2, duration: 2, repeat: Infinity }}
            >
              Click anywhere to continue
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LevelUpCelebration;
